import { useRouter } from 'expo-router';
import { Home } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Spacing } from '@/constants/theme';
import { generatePlan } from '@/lib/plan';
import { useOnboarding } from '@/store/onboarding';

const STATUS_LINES = [
  'Mapping your climate',
  'Reading your home systems',
  'Scheduling indoor maintenance',
  'Planning your grounds',
  'Tuning to first-frost dates',
  'Laying out your 12 months',
];

export default function Generating() {
  const router = useRouter();
  const state = useOnboarding();
  const [statusIdx, setStatusIdx] = useState(0);
  const didNavigate = useRef(false);

  // Pre-compute the plan so it's ready by the time we reveal.
  const plan = useMemo(
    () =>
      generatePlan({
        homeType: state.homeType,
        hvacType: state.hvacType,
        climateZone: state.climateZone,
        lat: state.lat,
        inside: state.inside,
        outside: state.outside,
        hasPool: state.hasPool,
        hasSolar: state.hasSolar,
        hasPets: state.hasPets,
        hasKids: state.hasKids,
        hasHoa: state.hasHoa,
        hasWoodfire: state.hasWoodfire,
        hasSmartHome: state.hasSmartHome,
        hasStrHost: state.hasStrHost,
        hasHeavySnow: state.hasHeavySnow,
        hasGarden: state.hasGarden,
        hasAccessibility: state.hasAccessibility,
        isFirstTime: state.isFirstTime,
        regionHazards: state.regionHazards,
      }),
    [
      state.homeType, state.hvacType, state.climateZone,
      state.inside, state.outside,
      state.hasPool, state.hasSolar, state.hasPets, state.hasKids,
      state.hasHoa, state.hasWoodfire, state.hasSmartHome, state.hasStrHost,
      state.hasHeavySnow, state.hasGarden, state.hasAccessibility, state.isFirstTime,
      state.regionHazards,
    ]
  );

  // Cache the computed plan on the store so the next screen can read it
  // without re-running the generator. We stash it on a module-level ref.
  generatedPlanCache.value = plan;

  const pulse = useSharedValue(0);
  useEffect(() => {
    pulse.value = withRepeat(
      withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.ease) }),
      -1,
      false
    );
  }, [pulse]);
  const pulseStyle = useAnimatedStyle(() => ({
    opacity: 1 - pulse.value * 0.85,
    transform: [{ scale: 1 + pulse.value * 0.9 }],
  }));

  useEffect(() => {
    const iv = setInterval(() => {
      setStatusIdx((i) => {
        if (i >= STATUS_LINES.length - 1) {
          clearInterval(iv);
          if (!didNavigate.current) {
            didNavigate.current = true;
            setTimeout(() => router.replace('/(onboarding)/plan' as never), 420);
          }
          return i;
        }
        return i + 1;
      });
    }, 560);
    return () => clearInterval(iv);
  }, [router]);

  return (
    <View style={styles.wrap}>
      <View style={styles.orb}>
        <Animated.View style={[styles.pulseRing, pulseStyle]} />
        <Home size={40} color={Palette.cream} strokeWidth={2.4} />
      </View>
      <Text style={styles.title}>Building your plan…</Text>
      <Text style={styles.status}>{STATUS_LINES[statusIdx]}</Text>
    </View>
  );
}

// Tiny module-level cache so plan.tsx can read the same generated plan.
export const generatedPlanCache: { value: ReturnType<typeof generatePlan> | null } = {
  value: null,
};

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.six,
  },
  orb: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: Palette.forest,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  pulseRing: {
    position: 'absolute',
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: Palette.forestGlow,
  },
  title: {
    marginTop: Spacing.six,
    fontFamily: Fonts.serifBold,
    fontSize: 21,
    color: Palette.ink,
  },
  status: {
    marginTop: Spacing.three,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.inkSoft,
    minHeight: 20,
  },
});
